import { useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Clock } from 'lucide-react';

type Frequency = 'daily' | 'weekly' | 'monthly' | 'custom';

interface CronBuilderProps {
  value: string; // Cron expression
  timezone: string;
  onChange: (cron: string) => void;
  onTimezoneChange: (timezone: string) => void;
}

const WEEKDAYS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const TIMEZONES = [
  { value: 'Asia/Makassar', label: 'WITA (Asia/Makassar)' },
  { value: 'Asia/Jakarta', label: 'WIB (Asia/Jakarta)' },
  { value: 'Asia/Jayapura', label: 'WIT (Asia/Jayapura)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'Europe/London', label: 'London' },
];

export function CronBuilder({ value, timezone, onChange, onTimezoneChange }: CronBuilderProps) {
  const [frequency, setFrequency] = useState<Frequency>('daily');
  const [hour, setHour] = useState('3');
  const [minute, setMinute] = useState('0');
  const [dayOfWeek, setDayOfWeek] = useState('1'); // Monday
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [customCron, setCustomCron] = useState(value || '0 3 * * *');

  // Use ref to avoid onChange in dependencies
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Parse existing cron expression on mount
  useEffect(() => {
    if (!value) return;

    const parts = value.split(' ');
    if (parts.length !== 5) return;

    const [min, hr, dom, _month, dow] = parts;

    setMinute(min);
    setHour(hr);

    // Detect frequency based on pattern
    if (dom === '*' && dow === '*') {
      setFrequency('daily');
    } else if (dom === '*' && dow !== '*') {
      setFrequency('weekly');
      setDayOfWeek(dow);
    } else if (dom !== '*' && dow === '*') {
      setFrequency('monthly');
      setDayOfMonth(dom);
    } else {
      setFrequency('custom');
      setCustomCron(value);
    }
  }, [value]);

  // Generate cron expression when inputs change (with debouncing)
  useEffect(() => {
    const timer = setTimeout(() => {
      let cron = '';

      switch (frequency) {
        case 'daily':
          cron = `${minute} ${hour} * * *`;
          break;
        case 'weekly':
          cron = `${minute} ${hour} * * ${dayOfWeek}`;
          break;
        case 'monthly':
          cron = `${minute} ${hour} ${dayOfMonth} * *`;
          break;
        case 'custom':
          cron = customCron;
          break;
      }

      if (cron && cron !== value) {
        onChangeRef.current(cron);
      }
    }, 500); // 500ms debounce - wait for user to finish typing

    return () => clearTimeout(timer);
  }, [frequency, hour, minute, dayOfWeek, dayOfMonth, customCron, value]);

  return (
    <div className="space-y-4">
      {/* Frequency Selector */}
      <div className="space-y-2">
        <Label>Frequency</Label>
        <Select value={frequency} onValueChange={(val) => setFrequency(val as Frequency)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="custom">Custom (Advanced)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Time Picker */}
      {frequency !== 'custom' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Hour (0-23)</Label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="number"
                min="0"
                max="23"
                value={hour}
                onChange={(e) => setHour(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Minute (0-59)</Label>
            <Input
              type="number"
              min="0"
              max="59"
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Day of Week Selector (Weekly) */}
      {frequency === 'weekly' && (
        <div className="space-y-2">
          <Label>Day of Week</Label>
          <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEEKDAYS.map((day) => (
                <SelectItem key={day.value} value={day.value}>
                  {day.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Day of Month Selector (Monthly) */}
      {frequency === 'monthly' && (
        <div className="space-y-2">
          <Label>Day of Month (1-31)</Label>
          <Input
            type="number"
            min="1"
            max="31"
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(e.target.value)}
          />
        </div>
      )}

      {/* Custom Cron Expression */}
      {frequency === 'custom' && (
        <div className="space-y-2">
          <Label>Cron Expression</Label>
          <Input
            value={customCron}
            onChange={(e) => setCustomCron(e.target.value)}
            placeholder="0 3 * * 1"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Format: minute hour day-of-month month day-of-week
          </p>
        </div>
      )}

      {/* Timezone Selector */}
      <div className="space-y-2">
        <Label>Timezone</Label>
        <Select value={timezone} onValueChange={onTimezoneChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONES.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Current Cron Display */}
      <div className="rounded-lg bg-muted p-3">
        <p className="text-xs font-medium text-muted-foreground mb-1">
          Cron Expression:
        </p>
        <code className="text-sm font-mono">{value || '0 3 * * *'}</code>
      </div>
    </div>
  );
}
